import React, { useContext } from 'react';
import { useChannelAdminContext } from 'hooks';
import { BulkAction, RowSelectionState, Button } from 'design-system-web';
import { useDownloadFile } from '../../../../../../hooks/useDownloadFile';
import { generatePath } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import { useTranslation } from 'react-i18next';
import { Invoice } from 'lane-shared/domains/billingPayments/types';
import { isInvoicePayable } from './isInvoicePayable';
import { useBulkPaidInvoicesEnabled } from 'react-hooks';
import { useBulkUpdateInvoicesToPaid } from './bulkUpdateInvoicesToPaidMutation';
import { UserDataContext } from 'lane-shared/contexts';

import styles from 'design-system-web/components/DataViewControls/DataViewControls.scss';

export function useGetBulkActionsForInvoices(
  invoices: Invoice[],
  refetch?: () => Promise<any>
): BulkAction[] {
  type InvoiceBulkDownloadRequest = {
    invoiceIds: string[];
  };

  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const { downloadBulkFilesFromUrl } =
    useDownloadFile<InvoiceBulkDownloadRequest>();
  const isBulkPaidInvoicesEnabled = useBulkPaidInvoicesEnabled();
  const { user } = useContext(UserDataContext);
  const { bulkUpdateInvoicesToPaid } = useBulkUpdateInvoicesToPaid();
  const INVOICE_SELECTION_LIMIT = 50;

  const getSelectedInvoices = (rowSelection: RowSelectionState): Invoice[] => {
    const selectedDisplayIds = Object.entries(rowSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([displayId]) => displayId);

    return invoices.filter(invoice =>
      selectedDisplayIds.includes(invoice.displayId)
    );
  };

  const showSuccessToast = async (
    successCount: number,
    failureCount: number
  ): Promise<void> => {
    if (failureCount === 0) {
      await window.Toast.show(
        t('abp.bulkActions.markAsPaid.allSuccess', {
          count: successCount,
        }),
        5000
      );
    } else {
      await window.Toast.show(
        t('abp.bulkActions.markAsPaid.partialSuccess', {
          successCount,
          failureCount,
        }),
        5000
      );
    }
  };

  const handleMarkAsPaidError = async (
    err: unknown,
    selectedInvoiceCount: number,
    payableInvoiceCount: number
  ): Promise<void> => {
    console.error('Failed to bulk mark invoices as paid', {
      selectedInvoiceCount,
      payableInvoiceCount,
      error: err,
    });

    window.Alert.hide();

    await window.Toast.show(
      t('abp.bulkActions.markAsPaid.error'),
      5000
    );
  };

  const handleMarkAsPaidClick = async (
    selectedInvoices: Invoice[],
    payableInvoices: Invoice[],
    isDisabled: boolean
  ): Promise<void> => {
    if (isDisabled) return;

    if (selectedInvoices.length > INVOICE_SELECTION_LIMIT) {
      await window.Toast.show(
        t('abp.bulkActions.markAsPaid.limitExceeded', {
          invoiceSelectionLimit: INVOICE_SELECTION_LIMIT,
          selectedInvoiceCount: selectedInvoices.length,
        }),
        5000
      );

      return;
    }

    try {
      await window.Alert.confirm({
        title: t('abp.bulkActions.markAsPaid.confirmTitle'),
        message: t('abp.bulkActions.markAsPaid.confirmMessage', {
          count: payableInvoices.length,
        }),
        confirmText: t('abp.actions.markAsPaid'),
      });
    } catch {
      // User cancelled the confirmation dialog - exit silently without showing error toast
      return;
    }

    try {
      // Show loading indicator
      window.Alert.loading({
        title: t('abp.bulkActions.markAsPaid.processing'),
        disableCloseOnBackgroundClick: true,
      });

      // Call the bulk update mutation
      const invoiceIds = payableInvoices.map(invoice => invoice.id);
      const result = await bulkUpdateInvoicesToPaid({
        invoiceIds,
        externalActorId: user?._id || '',
      });

      // Show success toast with results using mapped response
      const { successCount, failureCount } = result;

      window.Alert.hide();

      await showSuccessToast(successCount, failureCount);

      // Refetch invoices to update the table
      if (refetch) {
        await refetch();
      }
    } catch (err: unknown) {
      await handleMarkAsPaidError(
        err,
        selectedInvoices.length,
        payableInvoices.length
      );
    }
  };

  const bulkActions: BulkAction[] = [
    {
      label: 'abp.bulkActions.downloadInvoices',
      onClick: async (rowSelection: RowSelectionState) => {
        const selectedInvoiceIds = Object.entries(rowSelection)
          .filter(([_, isSelected]) => isSelected)
          .map(([invoiceId]) => invoiceId);

        if (selectedInvoiceIds.length > INVOICE_SELECTION_LIMIT) {
          await window.Toast.show(
            t('abp.bulkActions.downloadInvoices.limitExceeded', {
              invoiceSelectionLimit: INVOICE_SELECTION_LIMIT,
              selectedInvoiceCount: selectedInvoiceIds.length,
            }),
            5000
          );

          return;
        }

        window.Alert.loading({
          title: `${t('abp.bulkActions.downloadInvoices.preparingYourZipFile')}`,
          disableCloseOnBackgroundClick: true,
        });

        try {
          const url = `${generatePath(routes.bulkDownloadInvoicePdfs, {
            channelId: channel?._id || '',
          })}`;

          await downloadBulkFilesFromUrl({
            url,
            body: { invoiceIds: selectedInvoiceIds },
          });
        } catch {
          await window.Toast.show(t('abp.bulkActions.downloadInvoices.error'));
        }

        setTimeout(() => {
          window.Alert.hide();
        }, 500);
      },
    },
  ];

  if (isBulkPaidInvoicesEnabled) {
    bulkActions.push({
      renderAction: (rowSelection?: RowSelectionState) => {
        const selectedInvoices = getSelectedInvoices(rowSelection || {});
        const payableInvoices = selectedInvoices.filter(isInvoicePayable);
        const isDisabled = payableInvoices.length === 0;

        return (
          <Button
            key="mark-as-paid"
            className={styles.BulkActionButton}
            variant="secondary"
            size="large"
            disabled={isDisabled}
            onClick={() =>
              handleMarkAsPaidClick(
                selectedInvoices,
                payableInvoices,
                isDisabled
              )
            }
          >
            {t('abp.bulkActions.markAsPaid')}
          </Button>
        );
      },
    });
  }

  return bulkActions;
}
